/* Interfaces */
interface IDivaSearchHit {
    heading: string;
    url: string;
    preamble: string;
    published: string;
    lastUpdatedDate: string;
    lastUpdatedDateISO: string;
    hitType: string;
    contentTypeName: string;
    contentTypeDisplayName: string;
    subjects: string[];
}
interface IDivaSearchResult {
    searchHits: IDivaSearchHit[];
    subjects: IDivaFilterType[];
    contentTypes: IDivaFilterType[];
    totalHits: number;
}

interface IDivaTextContent {
    noKeywords: string;
    noResults: string;
    resultFor: string;
    resultsFor: string;
    updated: string;
    showMore: string;
    show: string;
    more: string;
    showing: string;
    ofTotally: string;
    hide: string;
    error: string;
    published: string;
    allHits: string;
}
interface IDivaFilterType {
    term: string;
    count: number;
    displayName: string;
}

/* HTML elements */
const divaSearchForm = document.querySelector('.search-page__form') as HTMLFormElement;
const divaSearchInput = document.querySelector('.search-page__input') as HTMLInputElement;
const divaSearchResultSection = document.querySelector('.search-page__result') as HTMLElement;
const divaSearchResultInfo = document.querySelector('.search-page__result-info') as HTMLParagraphElement;
const divaSearchPageLoading = document.querySelector('.search-page__loading') as HTMLElement;

/* Data */
const { divaresultsToShow, divaresultsToAdd, divacurrentLanguage, divaincludeText } = divaSearchForm.dataset;

/* Text content (translated text) */
const divaIncludeTextArray = divaincludeText?.split(', ') as string[];
const divaTextContent: IDivaTextContent = {
    noKeywords: divaIncludeTextArray[0],
    noResults: divaIncludeTextArray[1],
    resultFor: divaIncludeTextArray[2],
    resultsFor: divaIncludeTextArray[3],
    updated: divaIncludeTextArray[4],
    showMore: divaIncludeTextArray[5],
    show: divaIncludeTextArray[6],
    more: divaIncludeTextArray[7],
    showing: divaIncludeTextArray[8],
    ofTotally: divaIncludeTextArray[9],
    hide: divaIncludeTextArray[10],
    error: divaIncludeTextArray[11],
    published: divaIncludeTextArray[12],
    allHits: divaIncludeTextArray[13],
};

/* Global variables */
let divaQuery: string | null = null;
const divaApiTakeAmount = parseInt(divaresultsToShow!, 10) as number;
let divaApiFromYear: string | null = "";
let divaApiToYear: string | null = "";
let divaApiSortOrder: string | null = "dateIssued_sort_desc";
let currentVisibleCount = 0 as number;
let divaTotalHits = 0 as number;
let divaSearchIsLoading: boolean = false;

/* Functions – Reset and clear DOM */
const resetDivaSearchResult = (): void => {
    divaSearchInput.value = '';
    divaSearchResultSection.classList.add('sr-only');
    divaSearchResultInfo.textContent = divaTextContent.noKeywords;
};

const clearDivaResultList = (): void => {
    const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');
    const showMore: HTMLDivElement | null = document.querySelector('.search-page__show-more');

    if (resultList) {
        resultList.remove();
    }

    if (showMore) {
        showMore.remove();
    }
};

/* Functions – Get data from API */
const getDivaSearchResult = async (takeAll: boolean = false): Promise<IDivaSearchResult | void> => {
    let fromYear = divaApiFromYear;
    let toYear = divaApiToYear;
    const sortOrder = divaApiSortOrder;

    if (takeAll) {
        const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
        const yearFromFromParam: string | null = urlParams.get('from');
        const yearToFromParam: string | null = urlParams.get('to');

        if (yearFromFromParam) {
            fromYear = yearFromFromParam;
        }

        if (yearToFromParam) {
            toYear = yearToFromParam;
        }
    }

    //search with * if query is empty
    if (divaQuery?.length == 0) {
        divaQuery = "*";
    }

    let path = `/api/divasearch?query=${divaQuery}&language=${divacurrentLanguage}`;

    if (fromYear) {
        path += `&fromYear=${fromYear}`;
    }

    if (toYear) {
        path += `&toYear=${toYear}`;
    }

    if (sortOrder) {
        path += `&sortOrder=${sortOrder}`;
    }

    try {
        divaSearchIsLoading = true;
        toggleDivaLoadingSpinner();

        const response = await fetch(path, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });

        const result = await response.json();

        if (!response.ok) {
            throw new Error(response.statusText);
        }

        return result;
    } catch (error) {
        console.log(error);
        divaSearchResultInfo.textContent = divaTextContent.error;
        divaSearchResultSection.classList.remove('sr-only');
    } finally {
        divaSearchIsLoading = false;
        toggleDivaLoadingSpinner();
    }
};

const toggleDivaLoadingSpinner = (): void => {
    if (divaSearchIsLoading) {
        divaSearchPageLoading.classList.remove('hidden');
    } else {
        divaSearchPageLoading.classList.add('hidden');
    }
};

/* Functions – Render DOM */
const renderDivaResultItems = (searchHits: IDivaSearchHit[], resultList: HTMLUListElement): void => {

    const itemsPerPage = 10;
    currentVisibleCount = itemsPerPage;
    searchHits.forEach((item, index) => {
        const resultItem = document.createElement('li') as HTMLLIElement;
        resultItem.classList.add('search-page__result-item', 'site-page-alternative-partial', 'site-page-alternative-partial--search-hit');

        // Hide items beyond the first 10
        if (index >= itemsPerPage) {
            resultItem.style.display = 'none';
        }

        const contentWrapper = document.createElement('div') as HTMLDivElement;
        contentWrapper.classList.add('site-page-alternative-partial__content');

        const textContainer = document.createElement('div') as HTMLDivElement;
        textContainer.classList.add('site-page-alternative-partial__text-container');

        const textContentDiv = document.createElement('div') as HTMLDivElement;
        textContentDiv.classList.add('site-page-alternative-partial__text-content');

        const iconContainer = document.createElement('div') as HTMLDivElement;
        iconContainer.classList.add('site-page-alternative-partial__arrow-icon-container');

        const iconSpan = document.createElement('span') as HTMLSpanElement;
        iconSpan.classList.add('site-page-alternative-partial__arrow-icon', 'icon', 'icon--external');

        textContainer.appendChild(textContentDiv);
        iconContainer.appendChild(iconSpan);
        textContainer.appendChild(iconContainer);
        contentWrapper.appendChild(textContainer);

        /*** Date */
        const dateAndCategories = document.createElement('div') as HTMLDivElement;
        dateAndCategories.classList.add('site-page-alternative-partial__date-time-and-categories');

        const timeEl = document.createElement('time') as HTMLTimeElement;
        timeEl.classList.add('site-page-alternative-partial__date-time');
        timeEl.textContent = divaTextContent.published + " " + item.published;

        dateAndCategories.appendChild(timeEl);
        textContentDiv.appendChild(dateAndCategories);

        /*** Header / Title */
        const heading = document.createElement('h3') as HTMLHeadingElement;
        heading.classList.add('site-page-alternative-partial__heading', 'heading');

        // Add icon if document
        if (item?.contentTypeName === 'document') {
            heading.classList.add('icon', 'icon--pdf');
        }

        /*** Link */
        const link = document.createElement('a') as HTMLAnchorElement;
        link.classList.add('site-page-alternative-partial__link');
        link.setAttribute('href', item.url);
        link.setAttribute('target', '_blank');
        link.textContent = item.heading;
        heading.appendChild(link);
        textContentDiv.appendChild(heading);

        /*** Preamble */
        if (item.preamble) {
            const preamble = document.createElement('p') as HTMLParagraphElement;
            preamble.classList.add('site-page-alternative-partial__preamble');
            preamble.innerHTML = item.preamble;
            textContentDiv.appendChild(preamble);
        }

        resultItem.appendChild(contentWrapper);
        resultList.appendChild(resultItem);
    });
};

const renderDivaShowMoreButton = (searchHitsLeft: number): void => {
    const showMoreContainer = document.createElement('div');
    showMoreContainer.classList.add('search-page__show-more');

    const showMoreButton = document.createElement('button');
    showMoreButton.classList.add('search-page__show-more-button', 'button', 'button--primary');
    showMoreButton.setAttribute('type', 'button');

    if (divaresultsToAdd) {
        let numberOfHitsToShow = parseInt(divaresultsToAdd!, 10);

        if (searchHitsLeft < parseInt(divaresultsToAdd!, 10)) {
            numberOfHitsToShow = searchHitsLeft;
        }

        showMoreButton.textContent = `${divaTextContent.show} ${numberOfHitsToShow} ${divaTextContent.more}`;
    } else {
        showMoreButton.textContent = divaTextContent.showMore;
    }

    const iconSpan = document.createElement('span');
    iconSpan.classList.add('search-page__show-more-button-icon', 'icon', 'icon--icon-plus');
    showMoreButton.appendChild(iconSpan);

    showMoreButton.addEventListener('click', () => {
        addDivaMoreResultItems(showMoreButton);
    });

    showMoreContainer.appendChild(showMoreButton);
    divaSearchResultSection.appendChild(showMoreContainer);
};

const renderDivaResult = (searchResult: IDivaSearchResult): void => {
    clearDivaResultList();

    if (searchResult.totalHits > 0) {
        const resultList = document.createElement('ul') as HTMLUListElement;
        resultList.classList.add('search-page__result-list');
        divaSearchResultSection.appendChild(resultList);

        renderDivaResultItems(searchResult.searchHits, resultList);

        divaTotalHits = searchResult.totalHits;

        const searchHitsLeft = searchResult.totalHits - currentVisibleCount;

        let outOf = currentVisibleCount;

        if (currentVisibleCount > divaTotalHits) {
            outOf = divaTotalHits;
        }

        let srOnlySearchedFor = '';
        if (divaQuery && divaQuery !== '*') {
            srOnlySearchedFor = `${divaTextContent.resultsFor}: ${divaQuery}`;
        } else if (divaQuery === '*') {
            srOnlySearchedFor = divaTextContent.allHits;
        }

        if (searchResult.totalHits === 1) {
            divaSearchResultInfo.innerHTML = `${searchResult.totalHits} ${divaTextContent.resultFor} <strong>${divaQuery}</strong>.`;
        } else {
            divaSearchResultInfo.innerHTML = `${divaTextContent.showing} 1-${outOf} ${divaTextContent.ofTotally} ${searchResult.totalHits} <span class="sr-only">${srOnlySearchedFor}</span>`;
        }

        if (currentVisibleCount < searchResult.totalHits) {
            renderDivaShowMoreButton(searchHitsLeft);
        }
    } else {
        divaSearchResultInfo.textContent = divaTextContent.noResults;
    }

    divaSearchResultSection.classList.remove('sr-only');
};

/* Functions - Search */
const divaSearch = async (): Promise<void> => {
    const searchResult = await getDivaSearchResult();
    if (searchResult) {
        renderDivaResult(searchResult);
    }
};

const searchDivaFromQuery = async (): Promise<void> => {
    const searchResult = await getDivaSearchResult(true);
    if (searchResult) {
        renderDivaResult(searchResult);
    }
};

const searchDivaFromQueryString = (): void => {
    const urlParams = new URLSearchParams(window.location.search) as URLSearchParams;
    const queryFromParam: string | null = urlParams.get('query');
    const fromYearFromParam: string | null = urlParams.get('fromYear');
    const toYearFromParam: string | null = urlParams.get('toYear');
    const sortOrderFromParam: string | null = urlParams.get('sortOrder');

    divaQuery = queryFromParam;

    if (divaQuery) {
        divaQuery = divaQuery.trim();
        divaSearchInput.value = divaQuery;
    } else {
        divaQuery = '*';
    }

    if (fromYearFromParam) {
        divaApiFromYear = fromYearFromParam;
        //Set value in dropdown from query
        const yearFromSelect = document.getElementById("yearFrom") as HTMLSelectElement;
        yearFromSelect.value = fromYearFromParam;
    }

    if (toYearFromParam) {
        divaApiToYear = toYearFromParam;
        //Set value in dropdown from query
        const yearToSelect = document.getElementById("yearTo") as HTMLSelectElement;
        yearToSelect.value = toYearFromParam;
    }

    if (sortOrderFromParam) {
        divaApiSortOrder = sortOrderFromParam;
        //Set value in dropdown from query
        const sortOrderSelect = document.getElementById("sortOrder") as HTMLSelectElement;
        sortOrderSelect.value = sortOrderFromParam;
    }
    searchDivaFromQuery();
};

const addDivaMoreResultItems = async (showMoreButton: HTMLButtonElement): Promise<void> => {
    const resultList: HTMLUListElement | null = document.querySelector('.search-page__result-list');

    if (resultList) {
        const items = resultList.querySelectorAll('li');
        let shownCount = 0;

        items.forEach((item, index) => {
            if (index >= currentVisibleCount && shownCount < divaApiTakeAmount) {
                item.style.display = 'block';
                shownCount++;
            }
        });

        currentVisibleCount += divaApiTakeAmount;

        let outOf = currentVisibleCount;

        const hitsLeft = divaTotalHits - currentVisibleCount;
        let labelNumber = divaApiTakeAmount;

        if (hitsLeft > divaApiTakeAmount) {
            labelNumber = divaApiTakeAmount;
        } else if (hitsLeft < divaApiTakeAmount) {
            labelNumber = hitsLeft;
        }

        if (currentVisibleCount >= divaTotalHits) {
            showMoreButton.remove();
            outOf = divaTotalHits;
        } else {
            showMoreButton.textContent = `${divaTextContent.show} ${labelNumber} ${divaTextContent.more}`;
            const iconSpan = document.createElement('span');
            iconSpan.classList.add('search-page__show-more-button-icon', 'icon', 'icon--icon-plus');
            showMoreButton.appendChild(iconSpan);
        }

        divaSearchResultInfo.innerHTML = `${divaTextContent.showing} 1-${outOf} ${divaTextContent.ofTotally} ${divaTotalHits} <span class="sr-only">${divaTextContent.resultsFor}: ${divaQuery}</span>`;
    }
};

/*When user change values in dropdown lists */
const handleFilter = (event: Event): void => {
    const target = event.target as HTMLSelectElement;

    if (target.id === "yearFrom") {
        divaApiFromYear = target.value;
    }

    if (target.id === "yearTo") {
        divaApiToYear = target.value;
    }

    if (target.id === "sortOrder") {
        divaApiSortOrder = target.value;
    }

    divaQuery = divaSearchInput.value.trim();

    divaSearch();

    window.history.replaceState({}, '', `?query=${divaQuery}&fromYear=${divaApiFromYear}&toYear=${divaApiToYear}&sortOrder=${divaApiSortOrder}`);
};

/* Event listeners */

// Event that fires if someone enters url with query string
window.addEventListener('load', (): void => {
    const yearFromSelect = document.getElementById("yearFrom") as HTMLSelectElement;
    const yearToSelect = document.getElementById("yearTo") as HTMLSelectElement;
    const sortOrderSelect = document.getElementById("sortOrder") as HTMLSelectElement;

    if (yearFromSelect && yearToSelect && sortOrderSelect) {
        yearFromSelect.addEventListener("change", handleFilter);
        yearToSelect.addEventListener("change", handleFilter);
        sortOrderSelect.addEventListener("change", handleFilter);
    }

    searchDivaFromQueryString();

    divaSearchInput.focus();
});

// Event that fires when users browse through their browser history, e.g. use the back button.
window.addEventListener('popstate', (): void => {
    if (window.location.search) {
        searchDivaFromQueryString();
    } else if (!window.location.hash) {
        resetDivaSearchResult();
    }

    divaSearchInput.focus();
});

// Event that fires when user submits a search with the form
divaSearchForm.addEventListener('submit', (event: SubmitEvent): void => {
    event.preventDefault();
    divaTotalHits = 0;
    currentVisibleCount = 0;
    divaQuery = divaSearchInput.value.trim();

    window.history.replaceState({}, '', `?query=${divaQuery}&fromYear=${divaApiFromYear}&toYear=${divaApiToYear}&sortOrder=${divaApiSortOrder}`);

    divaSearch();
});